import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useRecipientAdapter } from "./use-adapter";
import type { Outcome, PassReason, RecipientBrief, StageId } from "./types";

/**
 * React Query bindings for the recipient token surface. The brief is fetched by
 * token; mutations invalidate it so the stage tracker, timeline, and expiry chip
 * reflect the new ledger state. Mirrors the signals hooks module.
 */
export function useRecipientBrief(
  token: string | null,
): UseQueryResult<RecipientBrief | null> {
  const adapter = useRecipientAdapter();
  return useQuery({
    queryKey: ["recipient-brief", token],
    queryFn: () => adapter.getBrief(token ?? ""),
    enabled: token != null,
    staleTime: 30_000,
  });
}

/**
 * Resolve the assigner identity for a dead token so the expired fallback can
 * name them (spec §2). Enabled only once the brief has resolved to null.
 */
export function useResolvedIdentity(
  token: string | null,
  enabled: boolean,
): UseQueryResult<{ assignerName: string } | null> {
  const adapter = useRecipientAdapter();
  return useQuery({
    queryKey: ["recipient-identity", token],
    queryFn: () => adapter.resolveIdentity(token ?? ""),
    enabled: token != null && enabled,
    staleTime: 30_000,
  });
}

export function useRecipientActions(token: string) {
  const adapter = useRecipientAdapter();
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["recipient-brief", token] });

  return {
    recordStage: useMutation({
      mutationFn: (stage: StageId) => adapter.recordStageEvent(token, stage),
      onSuccess: invalidate,
    }),
    recordOutcome: useMutation({
      mutationFn: ({ outcome, passReason }: { outcome: Outcome; passReason?: PassReason }) =>
        adapter.recordOutcome(token, outcome, passReason),
      onSuccess: invalidate,
    }),
    // No regressStage mutation: the token tier has no walk-back (R-1 / R-P4-5).
    // The bearer path 403s every stage_regressed, so the affordance and its
    // mutation were removed rather than left to fail on click.
    // Reopen a closed pursuit (outcome_reopened). Invalidates the brief so the
    // refetched projection reads the pursuit as active with the outcome cleared.
    reopenPursuit: useMutation({
      mutationFn: () => adapter.reopenPursuit(token),
      onSuccess: invalidate,
    }),
    snooze: useMutation({
      mutationFn: () => adapter.snooze(token),
      onSuccess: invalidate,
    }),
    relationshipCheck: useMutation({
      mutationFn: () => adapter.runRelationshipCheck(token),
      onSuccess: invalidate,
    }),
    tariffScan: useMutation({
      mutationFn: () => adapter.runTariffScan(token),
      onSuccess: invalidate,
    }),
    sendAsset: useMutation({
      mutationFn: (assetTitle: string) => adapter.sendAsset(token, assetTitle),
      onSuccess: invalidate,
    }),
    findContacts: useMutation({
      mutationFn: () => adapter.findContacts(token),
    }),
    setFollowUp: useMutation({
      mutationFn: (dueAt: string) => adapter.setFollowUp(token, dueAt),
      onSuccess: invalidate,
    }),
  };
}

export type RecipientActionMutations = ReturnType<typeof useRecipientActions>;
